import { useEffect, useState } from "react";
import { Eye, Plus } from "react-bootstrap-icons";
import { formatDateTime } from "@utils";
import { Input, Button } from "@components";
import TaggedUsers from "@components/TaggedUsers";
import { OntikBadge } from "@components/NoteSourceBadge";
import { createNote } from "@api";
import { useUser } from "@auth";

export default function Timeline({
  activity,
  paymentRequestId,
  customerId,
  onNoteAdded,
}) {
  const [noteFormOpen, setNoteFormOpen] = useState(false);
  const [content, setContent] = useState("");
  const { user } = useUser();
  const [liveActivity, setLiveActivity] = useState([]);

  useEffect(() => {
    setLiveActivity(
      activity.sort((a, b) => new Date(b.date) - new Date(a.date)),
    );
  }, [activity]);

  const onAddNote = async () => {
    await createNote(content, paymentRequestId, customerId);

    const newActivity = [
      {
        title: "Note added",
        // Freshly typed notes are always Ontik-sourced — badge them like reloaded ones.
        subTitle: (
          <span className="inline-flex items-center gap-1.5">
            <OntikBadge />
            <span>{`${formatDateTime(new Date())} - ${user.name}`}</span>
          </span>
        ),
        content: content,
        date: new Date(),
        nodeContent: { text: user.name.split(" ").map(n => n[0]).join("") },
      },
      ...liveActivity,
    ];

    setLiveActivity(newActivity);
    setNoteFormOpen(false);
    setContent("");
    if (onNoteAdded) {
      onNoteAdded();
    }
  };

  const Node = ({ icon: Icon, type, initial }) => {
    const classes = () => {
      if (type === "primary") return "bg-primary-500 text-white";
      if (type === "black") return "bg-black text-white";
      if (type === "red") return "bg-red-500 text-white";
      if (type === "success") return "bg-green-700 text-white"
      if (type === "amber") return "bg-amber-400 text-white";
      return "bg-zinc-100 border border-zinc-200";
    };

    return (
      <div className="bg-white h-11 justify-center flex items-center">
        <div className="relative size-6 items-center justify-center flex bg-white">
          {Icon ? (
            <div
              className={`flex size-6 items-center justify-center rounded-full ${classes()}`}
            >
              <Icon
                aria-hidden="true"
                className={`size-4 shrink-0 ${classes()} border-0`}
              />
            </div>
          ) : initial ? (
            <div className="flex size-6 items-center justify-center rounded-full bg-zinc-600 text-white text-xs">
              {initial}
            </div>
          ) : (
            <div className="flex size-2 items-center justify-center rounded-full bg-zinc-100 ring-1 ring-inset ring-zinc-300"></div>
          )}
        </div>
      </div>
    );
  };

  const monthKey = (date) => {
    const d = new Date(date);
    return `${d.getFullYear()}-${d.getMonth()}`;
  };

  const monthLabel = (date) => {
    return new Date(date).toLocaleDateString("en-GB", { month: "long", year: "numeric" });
  };

  return (
    <div className="flex-1 px-5 py-5 scrollbar-hidden">
      <div className="w-full flex mb-6 items-center">
        <p className="font-semibold m-0">Timeline</p>
        {(paymentRequestId || customerId) && (
          <Button
            className="ml-auto"
            size="xs"
            color="text"
            onClick={() => setNoteFormOpen(true)}
          >
            <Plus className="mr-1" />
            Add note
          </Button>
        )}
      </div>
      <ul role="list" className="">
        {noteFormOpen && (
          <li>
            <div className="relative pb-8">
              <div className="relative flex items-start space-x-2">
                <div className="relative">
                  <div className="flex size-6 items-center justify-center rounded-full bg-zinc-600 text-white text-xs">
                    {user.name
                      .split(" ")
                      .map((name) => name[0])
                      .join("")}
                  </div>
                </div>
                <div className="min-w-0 flex-1">
                  <Input
                    type="textarea"
                    className="h-32 p-3"
                    onChange={(e) => setContent(e.target.value)}
                    value={content}
                    placeholder="Add a note"
                  />
                  <div className="absolute flex gap-1 transition-all duration-200 ease-in-out bottom-1 right-2">
                    <Button color="text" onClick={() => setNoteFormOpen(false)}>
                      Cancel
                    </Button>
                    <Button color="secondary" onClick={() => onAddNote()}>
                      Add note
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </li>
        )}
        {liveActivity.map((activityItem, activityItemIdx) => {
          const showMonthHeader =
            activityItemIdx === 0 ||
            monthKey(activityItem.date) !== monthKey(liveActivity[activityItemIdx - 1].date);

          return (
            <li key={activityItemIdx}>
              {showMonthHeader && (
                <div className="relative flex items-center gap-3 mb-4">
                  <span className="text-xs font-semibold text-zinc-500 uppercase tracking-wide whitespace-nowrap">
                    {monthLabel(activityItem.date)}
                  </span>
                </div>
              )}
              <div className="relative pb-6">
                {activityItemIdx !== liveActivity.length - 1 &&
                  monthKey(activityItem.date) === monthKey(liveActivity[activityItemIdx + 1].date) ? (
                  <span
                    aria-hidden="true"
                    className="absolute left-3 top-10 -ml-px h-full w-0.5 bg-zinc-200"
                  />
                ) : null}
                <div className="relative flex items-start space-x-2">
                  <Node
                    icon={activityItem?.nodeContent?.icon}
                    type={activityItem?.nodeContent?.color}
                    initial={activityItem?.nodeContent?.text}
                  />
                  <div className="min-w-0 flex-1 py-0.5  mt-2.5">
                    <div className="text-sm flex gap-2 font-medium">
                      <div>{activityItem?.title}</div>
                      <div className="ml-auto whitespace-nowrap">
                        {activityItem?.amount}
                      </div>
                    </div>
                    <div className="text-xs text-zinc-400 mt-1">
                      {activityItem?.subTitle}
                    </div>
                    {activityItem?.onView && (
                      <button
                        onClick={activityItem.onView}
                        className="mt-1 flex items-center gap-1 text-xs font-medium text-zinc-400 hover:text-zinc-600"
                      >
                        <Eye className="h-3.5 w-3.5" />
                        View
                      </button>
                    )}
                    {/* pre-line keeps the author's line breaks (multi-paragraph notes) */}
                    <div className="text-sm mt-1 whitespace-pre-line">
                      {activityItem?.content}
                    </div>
                    <TaggedUsers users={activityItem?.taggedUsers} />
                  </div>
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
